import dayjs from 'dayjs'
import { get, isEmpty } from 'lodash'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import {
  apiEndpoints,
  fetchInterestedProgramsOptions,
  fetchResolutionConclusionsOptions,
  fetchTicketTypeOptions,
  getConfigValue,
  getProgramsList
} from 'api'
import BaseEntity from 'app/components/BaseEntity'
import {
  tableColumnDefaultOption,
  commonAttributes,
  navigationPaths
} from 'app/config'
import constants, {
  FOLLOW_UP_DATE_MESSAGE,
  TICKET_STATUS_OPTIONS
} from 'app/constants'
import {
  fetchTicketData,
  updateTicketData
} from 'app/store/actions/communicationEntity'
import { selectTicketsData } from 'app/store/selectors'
import { validateNotEmpty, validateNotNil } from 'app/validators'

import { columns } from './config'
const Ticket = () => {
  const [ticketTypeOptions, setTicketTypeOptions] = useState([])
  const [ticketStatusOptions, setTicketStatusOptions] = useState([])
  const [resolutionConclusionsOptions, setResolutionConclusionsOptions] =
    useState([])
  const [interestedProgramsOptions, setInterestedProgramsOptions] = useState([])
  const [isCreate, setIsCreate] = useState(false)

  const ticketData = useSelector(selectTicketsData)

  const [expandedRows, setExpandedRows] = React.useState({})
  const toggleRows = (rowsId) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [rowsId]: !prevExpandedRows[rowsId]
    }))
  }

  React.useEffect(() => {
    ;(async () => {
      fetchTicketTypes()
      fetchTicketStatuses()
      fetchResolutionConclusions()
      fetchInterestedPrograms()
    })()
  }, [])

  const fetchTicketTypes = async () => {
    try {
      const response = await fetchTicketTypeOptions()
      if (response) {
        const optionsArray = response?.results?.[0]?.value

        const optionsMapping =
          optionsArray?.length > 0
            ? optionsArray.map((item) => {
                return {
                  uuid: item.key,
                  name: item.label,
                  showOnlyInFilter: item.show_only_in_filter,
                  isRequireConclusion: item.requires_conclusion,
                  isIntentTicket: item?.enable_intent_section
                }
              })
            : {}
        setTicketTypeOptions(optionsMapping)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error.toString())
    }
  }

  const fetchTicketStatuses = async () => {
    try {
      const response = await getConfigValue({ key: 'CRM_TICKET_STATUSES' })
      if (response) {
        const optionsArray = response

        const optionsMapping =
          optionsArray?.length > 0
            ? optionsArray.map((item) => {
                return {
                  uuid: item,
                  name: item,
                  showInCreate: item == 'Resolved' ? false : true
                }
              })
            : {}

        setTicketStatusOptions(optionsMapping)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error.toString())
    }
  }

  const fetchResolutionConclusions = async () => {
    try {
      const response = await fetchResolutionConclusionsOptions()
      if (response) {
        const optionsArray = response?.results?.[0]?.value

        const optionsMapping =
          optionsArray.length > 0
            ? optionsArray.reduce((acc, item) => {
                acc[item] = item
                return acc
              }, {})
            : {}

        setResolutionConclusionsOptions(optionsMapping)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error.toString())
    }
  }

  const fetchInterestedPrograms = async () => {
    try {
      const response = await fetchInterestedProgramsOptions()
      if (response) {
        const programsArray = response?.results?.[0]?.value

        const programsMapping =
          programsArray?.length > 0
            ? programsArray.map((program) => ({
                uuid: program,
                name: program
              }))
            : []

        setInterestedProgramsOptions(programsMapping)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error.toString())
    }
  }

  const monthOptions = React.useMemo(() => {
    const options = []
    const start = dayjs().startOf('month')
    for (let i = 0; i <= 12; i++) {
      const d = start.add(i, 'month')
      options.push({
        uuid: d.endOf('month').format('YYYY-MM-DD'),
        name: d.format('MMM YYYY')
      })
    }
    return options
  }, [])

  const modifiedColumns = React.useMemo(() => {
    const updatedColumns = [
      {
        ...commonAttributes.uuid
      },
      ...columns.slice(0, 1),
      {
        name: 'user',
        label: 'Parent',
        widget: 'autocomplete',
        minCharactersToSearch: 10,
        modalEdit: true,
        viewOnlyInModal: true,
        defaultValue: () => {
          return new URLSearchParams(window.location.search).get('parent')
        },
        validate: validateNotNil,
        getStructuredValues: (data) => {
          const getItemStructure = (item) => {
            if (Object.prototype.hasOwnProperty.call(item, 'user')) {
              const { uuid, first_name, last_name, phone_number } = item?.user

              return {
                uuid: uuid,
                name: `${phone_number} (${first_name} ${last_name})`
              }
            } else {
              return {
                uuid: item.uuid,
                name: item.name
              }
            }
          }
          if (Array.isArray(data) && !isEmpty(data)) {
            return [getItemStructure(data[0])]
          } else if (Object.prototype.hasOwnProperty.call(data, 'user')) {
            return [getItemStructure(data)]
          } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
            return data.results.map((item) => {
              return getItemStructure(item)
            })
          } else {
            return data.map((item) => {
              return getItemStructure(item)
            })
          }
        },
        getFormDataValue: (data) => {
          const getItemStructure = (item) => {
            if (Object.prototype.hasOwnProperty.call(item, 'user')) {
              return item.user.uuid
            }
          }
          if (Object.prototype.hasOwnProperty.call(data, 'user')) {
            return getItemStructure(data)
          }
        },
        getOptionLabelKey: 'name',
        getOptionSelectedKey: 'uuid',
        resultStructure: {
          keyName: 'user',
          keyValue: 'uuid'
        },
        apiOptions: {
          method: 'get',
          url: apiEndpoints.parent
        },
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'user_name',
          customBodyRender: (value) => {
            return value ? (
              <span>
                <a
                  href={`${navigationPaths.parentdashboard}?parent=${value?.user.uuid}`}>
                  {value?.user.full_name}
                </a>
              </span>
            ) : null
          }
        }
      },
      ...columns.slice(1, 3),
      {
        name: 'status',
        label: 'Ticket Status',
        widget: 'autocomplete',
        widgetFilter: true,
        valueLabelMappings: ticketStatusOptions,
        modalEdit: true,
        validate: validateNotEmpty,
        defaultValue: (data) => {
          return get(data, 'status') || ticketStatusOptions?.[0]?.uuid
        },
        getStructuredValues: (data) => {
          const getItemStructure = (item) => {
            return item && typeof item === 'string'
              ? { uuid: item, name: item }
              : item && (isCreate ? item.showInCreate : true)
              ? {
                  uuid: item.uuid,
                  name: item.name
                }
              : null
          }

          if (Array.isArray(data)) {
            return data?.map((item) => {
              return getItemStructure(item)
            })
          } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
            return data?.results?.map((item) => {
              return getItemStructure(item)
            })
          }

          return [getItemStructure(data)]
        },
        getFormDataValue: (data) => {
          const getItemStructure = (item) => item
          return getItemStructure(data)
        },
        getOptionLabelKey: 'name',
        getOptionSelectedKey: 'uuid',
        resultStructure: {
          keyName: 'status',
          keyValue: 'uuid'
        },
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'status',
          setCellProps: () => ({
            style: {
              minWidth: '240px',
              maxWidth: '240px'
            }
          })
        }
      },
      {
        name: 'type',
        label: 'Ticket Type',
        widget: 'autocomplete',
        widgetFilter: true,
        isValueKey: true,
        valueLabelMappings: ticketTypeOptions,
        modalEdit: true,
        editable: isCreate ? true : false,
        validate: validateNotEmpty,
        getStructuredValues: (data) => {
          const getItemStructure = (item) => {
            return item && typeof item === 'string'
              ? { uuid: item, name: item }
              : item && (isCreate ? !item?.showOnlyInFilter : true)
              ? {
                  uuid: item.uuid,
                  name: item.name
                }
              : null
          }

          if (Array.isArray(data)) {
            return data?.map((item) => {
              return getItemStructure(item)
            })
          } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
            return data?.results?.map((item) => {
              return getItemStructure(item)
            })
          }

          return [getItemStructure(data)]
        },
        getFormDataValue: (data) => {
          const getItemStructure = (item) => item
          return getItemStructure(data)
        },
        getOptionLabelKey: 'name',
        getOptionSelectedKey: 'uuid',
        resultStructure: {
          keyName: 'type',
          keyValue: 'uuid'
        },
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'type',
          setCellProps: () => ({
            style: {
              minWidth: '300px',
              maxWidth: '300px'
            }
          })
        }
      },
      ...columns.slice(3, 4),
      {
        name: 'description',
        label: 'ticket description',
        validate: validateNotEmpty,
        modalEdit: true,
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '400px',
              maxWidth: '400px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRenderLite: (dataIndex) => {
            const data = ticketData?.data[dataIndex]?.description
            const truncatedData = data?.slice(0, 100)
            const isTruncated = data?.length > truncatedData?.length
            const isExpanded = expandedRows[dataIndex]
            const displayData = isExpanded ? data : truncatedData

            return (
              <div>
                <span>
                  {!displayData
                    ? 'N/A'
                    : displayData?.length < 100
                    ? displayData
                    : !isExpanded
                    ? displayData + '...'
                    : displayData}
                  <br />
                  {isTruncated && (
                    <div style={{ textAlign: 'right' }}>
                      <a
                        href="#"
                        onClick={() => toggleRows(dataIndex)}
                        style={{ textDecoration: 'none', cursor: 'pointer' }}>
                        {isExpanded ? 'show less' : 'show more'}
                      </a>
                    </div>
                  )}
                </span>
              </div>
            )
          }
        }
      },
      ...columns.slice(4),
      {
        name: 'resolution_conclusion',
        label: 'Resolution Conclusion',
        widget: 'select',
        // isValueKey: true,
        widgetFilter: false,
        valueLabelMappings: resolutionConclusionsOptions,
        disabledInForm: false,
        modalEdit: true,
        validate: validateNotEmpty,
        options: {
          ...tableColumnDefaultOption.options,
          // filterKey: 'resolution_conclusion',
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '300px',
              maxWidth: '300px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRender: (value) => {
            return value?.toString() ? <span>{value}</span> : null
          }
        },
        conditionalRender: (data) => {
          return (
            get(data, 'status') === TICKET_STATUS_OPTIONS.RESOLVED &&
            ticketTypeOptions?.filter(
              (item) => item.uuid === get(data, 'type')
            )?.[0]?.isRequireConclusion
          )
        }
      },
      {
        name: 'resolution_summary',
        label: 'resolution summary',
        validate: validateNotEmpty,
        modalEdit: true,
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '400px',
              maxWidth: '400px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRenderLite: (dataIndex) => {
            const data = ticketData?.data[dataIndex]?.resolution_summary
            const truncatedData = data?.slice(0, 100)
            const isTruncated = data?.length > truncatedData?.length
            const isExpanded = expandedRows[dataIndex]
            const displayData = isExpanded ? data : truncatedData

            return (
              <div>
                <span>
                  {!displayData
                    ? 'N/A'
                    : displayData?.length < 100
                    ? displayData
                    : !isExpanded
                    ? displayData + '...'
                    : displayData}
                  <br />
                  {isTruncated && (
                    <div style={{ textAlign: 'right' }}>
                      <a
                        href="#"
                        onClick={() => toggleRows(dataIndex)}
                        style={{ textDecoration: 'none', cursor: 'pointer' }}>
                        {isExpanded ? 'show less' : 'show more'}
                      </a>
                    </div>
                  )}
                </span>
              </div>
            )
          }
        },
        conditionalRender: (data) => {
          return get(data, 'status') === TICKET_STATUS_OPTIONS.RESOLVED
        }
      },
      {
        name: 'follow_up_text',
        label: 'follow_up_text',
        widget: 'string',
        modalEdit: true,
        viewOnlyInModal: true,
        disabledInFormOnCreate: true,
        message: {
          text: FOLLOW_UP_DATE_MESSAGE,
          style: {
            marginLeft: '10px',
            marginBottom: '10px',
            color: 'black',
            fontSize: '0.9rem',
            fontFamily: 'Avenir'
          }
        },
        conditionalRender: (data) =>
          dayjs(get(data, 'follow_up_date')).isAfter(dayjs()),
        options: {
          ...tableColumnDefaultOption.options,
          display: false
        }
      },
      {
        name: 'conversion_intent',
        label: 'Intent',
        widget: 'select',
        modalEdit: true,
        validate: validateNotEmpty,
        valueLabelMappings: {
          High: 'High',
          Medium: 'Medium',
          Low: 'Low'
        },
        conditionalRender: (data) => {
          const ticketType = ticketTypeOptions?.find(
            (item) => item.uuid === get(data, 'type')
          )
          console.log(ticketType, 'ticketType')
          return ticketType?.isIntentTicket ?? false
        },
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'conversion_intent',
          setCellProps: () => ({
            style: {
              minWidth: '150px',
              maxWidth: '150px'
            }
          }),
          customBodyRender: (value) => {
            return value ? <span>{value}</span> : <span>N/A</span>
          }
        }
      },
      {
        name: 'interested_programs',
        label: 'Program Interested In',
        widget: 'autocomplete',
        modalEdit: true,
        validate: validateNotEmpty,
        minCharactersToSearch: 0,
        defaultValue: () => [],
        conditionalRender: (data) => {
          const ticketType = ticketTypeOptions?.find(
            (item) => item.uuid === get(data, 'type')
          )
          return (
            ticketType?.isIntentTicket === true &&
            (get(data, 'conversion_intent') === 'High' ||
              get(data, 'conversion_intent') === 'Medium')
          )
        },
        valueLabelMappings: interestedProgramsOptions,
        getStructuredValues: (data) => {
          const getItemStructure = (item) => {
            return {
              uuid: item?.uuid || item,
              name: item?.name || item
            }
          }
          if (Array.isArray(data)) {
            return data.map((item) => {
              return getItemStructure(item)
            })
          } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
            return [getItemStructure(data)]
          } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
            return data.results.map((item) => {
              return getItemStructure(item)
            })
          } else {
            return data.map((item) => {
              return getItemStructure(item)
            })
          }
        },
        getFormDataValue: (data) => {
          const getItemStructure = (item) => item?.uuid || item
          return data.map((item) => getItemStructure(item))
        },
        getOptionLabelKey: 'name',
        getOptionSelectedKey: 'uuid',
        resultStructure: {
          keyName: 'interested_programs',
          keyValue: 'uuid'
        },
        multiple: true,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'interested_programs',
          setCellProps: () => ({
            style: {
              minWidth: '300px',
              maxWidth: '300px'
            }
          }),
          customBodyRender: (value) => {
            if (!value || (Array.isArray(value) && value.length === 0)) {
              return <span>N/A</span>
            }
            if (Array.isArray(value)) {
              const names = value
                .map((p) => (typeof p === 'string' ? p : p?.name))
                .filter(Boolean)
              return <span>{names.join(', ')}</span>
            }
            return (
              <span>
                {typeof value === 'string' ? value : value?.name || 'N/A'}
              </span>
            )
          }
        }
      },
      {
        name: 'tentative_joining_date',
        label: 'Tentative Joining Month',
        widget: 'autocomplete',
        modalEdit: true,
        validate: validateNotEmpty,
        minCharactersToSearch: 0,
        conditionalRender: (data) => {
          const ticketType = ticketTypeOptions?.find(
            (item) => item.uuid === get(data, 'type')
          )
          return (
            ticketType?.isIntentTicket === true &&
            (get(data, 'conversion_intent') === 'High' ||
              get(data, 'conversion_intent') === 'Medium')
          )
        },
        valueLabelMappings: monthOptions,
        getStructuredValues: (data) => {
          const getItemStructure = (item) => {
            const iso = item?.uuid || item
            return {
              uuid: iso,
              name: dayjs(iso, ['YYYY-MM-DD', 'DD-MM-YYYY']).format('MMM YYYY')
            }
          }
          if (Array.isArray(data)) {
            return data.map((item) => getItemStructure(item))
          } else if (Object.prototype.hasOwnProperty.call(data, 'uuid')) {
            return [getItemStructure(data)]
          } else if (Object.prototype.hasOwnProperty.call(data, 'results')) {
            return data.results.map((item) => getItemStructure(item))
          }
          return [getItemStructure(data)]
        },
        getFormDataValue: (data) => {
          // Always send YYYY-MM-DD
          return data?.uuid || data
        },
        getOptionLabelKey: 'name',
        getOptionSelectedKey: 'uuid',
        resultStructure: {
          keyName: 'tentative_joining_date',
          keyValue: 'uuid'
        },
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'tentative_joining_date',
          setCellProps: () => ({
            style: {
              minWidth: '200px',
              maxWidth: '200px'
            }
          }),
          customBodyRender: (value) => {
            if (!value) return <span>N/A</span>
            const parsed = dayjs(value, ['YYYY-MM-DD', 'DD-MM-YYYY'], true)
            return (
              <span>
                {parsed.isValid() ? parsed.format('MMM YYYY') : 'N/A'}
              </span>
            )
          }
        }
      },
      {
        name: 'intent_reason',
        label: 'What reasons indicate the selected intent?',
        widget: 'textarea',
        modalEdit: true,
        validate: validateNotEmpty,
        maxLength: 800,
        conditionalRender: (data) => {
          const ticketType = ticketTypeOptions?.find(
            (item) => item.uuid === get(data, 'type')
          )
          return ticketType?.isIntentTicket ?? false
        },
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '400px',
              maxWidth: '400px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRenderLite: (dataIndex) => {
            const data = ticketData?.data[dataIndex]?.intent_reason
            const truncatedData = data?.slice(0, 100)
            const isTruncated = data?.length > truncatedData?.length
            const isExpanded = expandedRows[dataIndex]
            const displayData = isExpanded ? data : truncatedData

            return (
              <div>
                <span>
                  {!displayData
                    ? 'N/A'
                    : displayData?.length < 100
                    ? displayData
                    : !isExpanded
                    ? displayData + '...'
                    : displayData}
                  <br />
                  {isTruncated && (
                    <div style={{ textAlign: 'right' }}>
                      <a
                        href="#"
                        onClick={() => toggleRows(dataIndex)}
                        style={{ textDecoration: 'none', cursor: 'pointer' }}>
                        {isExpanded ? 'show less' : 'show more'}
                      </a>
                    </div>
                  )}
                </span>
              </div>
            )
          }
        }
      }
    ]
    return updatedColumns
  }, [
    ticketData,
    expandedRows,
    ticketTypeOptions,
    ticketStatusOptions,
    interestedProgramsOptions,
    isCreate
  ])

  return (
    <>
      <BaseEntity
        entity={constants.TICKET}
        label={constants.TICKET_LABEL}
        columns={modifiedColumns}
        data={ticketData}
        fetchData={fetchTicketData}
        updateEntityData={updateTicketData}
        setIsCreateClicked={setIsCreate}
        disableDelete
      />
    </>
  )
}

export default Ticket
